import {
  BrandKitField,
  BrandKitLimits,
  brandKitKeyFromLabel,
  emptyBrandKitField,
} from "@arrowsup/platform-dtos";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { fieldTypeOptions, parseTypeOption } from "./brand-kit-field-editor";

interface Props {
  open: boolean;
  existingKeys: string[];
  onClose: () => void;
  onAdd: (field: BrandKitField) => void;
}

export const AddFieldDialog: React.FC<Props> = ({
  open,
  existingKeys,
  onClose,
  onAdd,
}) => {
  const [label, setLabel] = useState("");
  const [typeOption, setTypeOption] = useState(fieldTypeOptions[0].value);
  const [description, setDescription] = useState("");

  const trimmed = label.trim();
  const key = trimmed ? brandKitKeyFromLabel(trimmed, existingKeys) : "";

  const close = () => {
    setLabel("");
    setTypeOption(fieldTypeOptions[0].value);
    setDescription("");
    onClose();
  };

  const add = () => {
    const { type, itemType } = parseTypeOption(typeOption);
    onAdd(
      emptyBrandKitField(
        { key, label: trimmed, description: description.trim() },
        type,
        itemType
      )
    );
    close();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Add field</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            inputProps={{ maxLength: BrandKitLimits.labelLength }}
            helperText={key ? `Key: ${key} (can't be changed later)` : " "}
            autoFocus
            fullWidth
          />
          <TextField
            select
            label="Type"
            value={typeOption}
            onChange={(e) => setTypeOption(e.target.value)}
            fullWidth
          >
            {fieldTypeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: BrandKitLimits.descriptionLength }}
            helperText="How to use this field. Shown to editors and to AI tools."
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={!trimmed} onClick={add}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
